/** Tab 11: Documents — real binary upload via /joining/{id}/documents/upload */
import { useState } from 'react'
import {
  Alert,
  Button, Card, Checkbox, Form, Input, message, Modal, Select, Space, Table, Tag, Tooltip, Upload,
} from 'antd'
import {
  EyeOutlined, InboxOutlined, PlusOutlined, UploadOutlined,
} from '@ant-design/icons'
import type { RcFile } from 'antd/es/upload'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { useAuthStore } from '@/features/auth/store'
import {
  joiningApi, type JoiningDetail, type DocumentRow, type DocStatus,
} from '@/features/hr/joiningApi'

interface Props { detail: JoiningDetail }

const STATUS_COLOR: Record<DocStatus, string> = {
  PENDING: 'default',
  RECEIVED: 'blue',
  VERIFIED: 'green',
  REJECTED: 'red',
  EXPIRED: 'orange',
}

const DOC_TYPES = [
  'AADHAAR', 'PAN', 'PASSPORT', 'VOTER_ID', 'DRIVING_LICENSE',
  'BANK_PROOF', 'EDUCATION_CERT', 'EXPERIENCE_CERT',
  'PHOTO_ID', 'ADDRESS_PROOF', 'OTHER',
]

/**
 * Expected ID-number formats, mirroring the server-side validation in
 * joining_service._ID_FORMATS. The backend rejects a mismatch on upload —
 * showing the pattern here lets the operator get it right the first time
 * instead of round-tripping to a rejection.
 */
const ID_FORMAT_HINT: Record<string, { pattern: RegExp; hint: string }> = {
  PAN: { pattern: /^[A-Z]{5}\d{4}[A-Z]$/, hint: 'Format: ABCDE1234F (5 letters, 4 digits, 1 letter)' },
  AADHAAR: { pattern: /^\d{12}$/, hint: 'Format: 12 digits (no spaces or dashes)' },
  VOTER_ID: { pattern: /^[A-Z]{3}\d{7}$/, hint: 'Format: ABC1234567 (3 letters, 7 digits)' },
}

/**
 * Documents that are mandatory before activation, mirroring the activation
 * checklist's DOCUMENTS/KYC/BANK required items (seed_joining_defaults.py):
 *   KYC_UPLOADED    → any Aadhaar / PAN / Voter / Passport
 *   PHOTO_UPLOADED  → employee photo (PHOTO_ID)
 *   BANK_VERIFIED   → bank proof
 * Last-salary / experience proof is intentionally NOT here (not compulsory
 * for workers).
 */
const MANDATORY_DOC_TYPES = new Set([
  'AADHAAR', 'PAN', 'PASSPORT', 'VOTER_ID', 'PHOTO_ID', 'BANK_PROOF',
])

const RequiredMark = () => (
  <Tooltip title="Mandatory before activation">
    <span style={{ color: '#ff4d4f' }}> *</span>
  </Tooltip>
)

const ALLOWED_EXT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif'
const MAX_BYTES = 2560 * 1024  // 2.5 MB (server compresses images further)

/**
 * Upload a document binary + metadata in one multipart POST.
 * Backend saves to backend/static/employee-documents/<emp_id>/<uuid>.<ext>
 * and creates an EmployeeDocument row with status=RECEIVED.
 */
async function uploadDocument(
  employeeId: string,
  file: File,
  docType: string,
  docNumber?: string,
  remarks?: string,
  keepOriginal = false,
): Promise<DocumentRow> {
  const form = new FormData()
  form.append('file', file, file.name)
  form.append('doc_type', docType)
  if (docNumber) form.append('doc_number', docNumber)
  if (remarks) form.append('remarks', remarks)
  form.append('keep_original', keepOriginal ? 'true' : 'false')
  const { data } = await apiClient.post<DocumentRow>(
    `/joining/${employeeId}/documents/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export default function DocumentsTab({ detail }: Props) {
  const qc = useQueryClient()
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const [addOpen, setAddOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectDocId, setRejectDocId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [addForm] = Form.useForm()
  // Watch the selected doc type so we can show its expected ID-number format.
  const selectedDocType = Form.useWatch('doc_type', addForm) as string | undefined
  const idFormat = selectedDocType ? ID_FORMAT_HINT[selectedDocType] : undefined

  const upload = useMutation({
    mutationFn: (args: {
      file: File
      doc_type: string
      doc_number?: string
      remarks?: string
      keep_original?: boolean
    }) =>
      uploadDocument(
        detail.id, args.file, args.doc_type, args.doc_number,
        args.remarks, args.keep_original,
      ),
    onSuccess: (doc) => {
      // Tell the user what the server actually did with the bytes
      const sizeKB = Math.round((doc.file_path ? (doc as { file_size?: number }).file_size ?? 0 : 0) / 1024)
      const remarksHint = (doc.remarks ?? '').split('\n').pop() ?? ''
      message.success(
        sizeKB
          ? `Document uploaded · ${sizeKB} KB stored${remarksHint.includes('saved') ? ` · ${remarksHint}` : ''}`
          : 'Document uploaded',
      )
      setAddOpen(false)
      setPendingFile(null)
      addForm.resetFields()
      void qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail ?? 'Upload failed')
    },
  })

  const verifyDoc = useMutation({
    mutationFn: (args: { docId: string; status: 'VERIFIED' | 'REJECTED'; reason?: string }) =>
      joiningApi.verifyDocument(args.docId, args.status, args.reason),
    onSuccess: () => {
      message.success('Document updated')
      setRejectOpen(false)
      setRejectDocId(null)
      setRejectReason('')
      void qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const validateFile = (file: RcFile): boolean => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      message.error('Only PDF, JPG, PNG or WEBP files are allowed')
      return false
    }
    if (file.size > MAX_BYTES) {
      message.error(`File too large (${Math.round(file.size / 1024)} KB). Max 2.5 MB.`)
      return false
    }
    return true
  }

  const onSubmit = (values: {
    doc_type: string
    doc_number?: string
    remarks?: string
    keep_original?: boolean
  }) => {
    if (!pendingFile) {
      message.error('Please select a file to upload')
      return
    }
    upload.mutate({ file: pendingFile, ...values })
  }

  const cols = [
    {
      title: 'Type',
      dataIndex: 'doc_type',
      render: (v: string) => (
        <span>
          <Tag>{v.replace(/_/g, ' ')}</Tag>
          {MANDATORY_DOC_TYPES.has(v) && <RequiredMark />}
        </span>
      ),
      width: 140,
    },
    { title: 'Number', dataIndex: 'doc_number', width: 140 },
    {
      title: 'File',
      dataIndex: 'file_path',
      render: (path: string | null, row: DocumentRow) =>
        path ? (
          <Tooltip title={row.doc_name ?? path}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              href={`/static/${path}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </Button>
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        ),
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: DocStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
      width: 110,
    },
    {
      title: 'Verified At',
      dataIndex: 'verified_at',
      render: (v: string | null) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—'),
      width: 160,
    },
    {
      title: 'Actions',
      render: (_: unknown, row: DocumentRow) => {
        if (row.status === 'PENDING' || row.status === 'RECEIVED') {
          // Maker ≠ checker: the person who uploaded a document cannot verify
          // their own upload (server enforces this too — verify_document raises
          // if created_by == the acting user). Grey the button out so the
          // operator sees why before the API rejects it.
          const isOwnUpload = !!currentUserId && row.created_by === currentUserId
          return (
            <Space size="small">
              <Tooltip
                title={isOwnUpload
                  ? 'You uploaded this document — another user must verify it'
                  : ''}
              >
                <Button
                  size="small"
                  type="primary"
                  disabled={isOwnUpload}
                  onClick={() => verifyDoc.mutate({ docId: row.id, status: 'VERIFIED' })}
                >
                  Verify
                </Button>
              </Tooltip>
              <Button
                size="small"
                danger
                onClick={() => {
                  setRejectDocId(row.id)
                  setRejectOpen(true)
                }}
              >
                Reject
              </Button>
            </Space>
          )
        }
        return null
      },
    },
  ]

  return (
    <Card
      size="small"
      title="Documents"
      extra={
        <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => setAddOpen(true)}>
          Add Document
        </Button>
      }
    >
      <Table<DocumentRow>
        rowKey="id"
        size="small"
        dataSource={detail.documents}
        columns={cols}
        pagination={false}
      />
      <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
        <span style={{ color: '#ff4d4f' }}>*</span> mandatory before activation
        (KYC — Aadhaar / PAN / Voter / Passport, employee photo, bank proof)
      </div>

      <Modal
        title="Upload Document"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false)
          setPendingFile(null)
          addForm.resetFields()
        }}
        onOk={() => addForm.submit()}
        confirmLoading={upload.isPending}
        okText="Upload"
        okButtonProps={{ disabled: !pendingFile }}
        width={520}
      >
        <Form form={addForm} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="doc_type" label="Document Type" rules={[{ required: true }]}>
            <Select
              placeholder="Choose type"
              options={DOC_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))}
              optionRender={(opt) => (
                <span>
                  {opt.label}
                  {MANDATORY_DOC_TYPES.has(String(opt.value)) && <RequiredMark />}
                </span>
              )}
            />
          </Form.Item>
          <Form.Item
            name="doc_number"
            label="Document Number"
            help={idFormat?.hint}
            rules={idFormat ? [{
              validator: (_r, value: string) => {
                if (!value) return Promise.resolve()  // number is optional
                return idFormat.pattern.test(value.trim().toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error(idFormat.hint))
              },
            }] : []}
            normalize={(v: string) => (
              // ID numbers are uppercase, no spaces/dashes — normalise as typed
              // so PAN/Voter match the pattern and Aadhaar drops separators.
              idFormat && typeof v === 'string' ? v.replace(/[\s-]/g, '').toUpperCase() : v
            )}
          >
            <Input
              placeholder={
                selectedDocType === 'PAN' ? 'ABCDE1234F'
                  : selectedDocType === 'AADHAAR' ? '123456789012'
                  : selectedDocType === 'VOTER_ID' ? 'ABC1234567'
                  : 'e.g. document number'
              }
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                File <span style={{ color: '#ff4d4f' }}>*</span>
              </span>
            }
            required
            help="PDF, JPG, PNG, or WEBP up to 10 MB"
          >
            <Upload.Dragger
              accept={ALLOWED_EXT}
              multiple={false}
              maxCount={1}
              showUploadList={true}
              fileList={pendingFile ? [{
                uid: '-1',
                name: pendingFile.name,
                status: 'done' as const,
                size: pendingFile.size,
              }] : []}
              beforeUpload={(file) => {
                if (!validateFile(file)) return Upload.LIST_IGNORE
                setPendingFile(file)
                return false  // prevent auto-upload — we submit on form OK
              }}
              onRemove={() => {
                setPendingFile(null)
                return true
              }}
              style={{ padding: '8px' }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 14 }}>
                Click or drag file here to select
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                PDF or image. Max 10 MB. One file only.
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item name="remarks" label="Remarks (optional)">
            <Input.TextArea rows={2} placeholder="Any notes about this document" />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Image documents are auto-compressed on save"
            description={
              <span style={{ fontSize: 12 }}>
                Image scans (jpg/png/heic) are resized to 1920px max and re-encoded
                to JPEG q85 — typically <b>70-90% smaller</b> with no visible quality
                loss for KYC purposes. PDFs are stored as-is. Tick the box below for
                signed contracts or legal documents that need pixel-perfect originals.
              </span>
            }
          />
          <Form.Item
            name="keep_original"
            valuePropName="checked"
            initialValue={false}
            style={{ marginBottom: 0 }}
          >
            <Checkbox>
              <span>Keep original quality (no compression)</span>
              <div style={{ fontSize: 11, color: '#888', marginLeft: 24 }}>
                Use for signed contracts, government forms, or anything legally binding.
              </div>
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject Document"
        open={rejectOpen}
        onCancel={() => {
          setRejectOpen(false)
          setRejectDocId(null)
          setRejectReason('')
        }}
        onOk={() => {
          if (rejectDocId) {
            verifyDoc.mutate({ docId: rejectDocId, status: 'REJECTED', reason: rejectReason })
          }
        }}
        confirmLoading={verifyDoc.isPending}
        okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
        okText="Reject"
      >
        <p>The worker will need to re-submit this document. Reason is logged in the audit trail.</p>
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. document is unclear / wrong type / expired"
        />
      </Modal>
    </Card>
  )
}

// Marker to silence unused-import lint when Upload icon variant isn't used directly
void UploadOutlined
